import { CommonModule } from "@angular/common";
import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { trimmedRequired } from "../../shared/validators/trimmed-required.validator";
import { nameValidator } from "../../shared/name-validator.directive";
import { digitsOnly } from "../../shared/validators/digits-only.validator";
import { indianPincode } from "../../shared/validators/indian-pincode.validator";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { indianPhoneNumber } from "../../shared/validators/indian-phone-number.validator";
import { UserService } from "../../services/user/user.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { BaseAddress, UserDetailsDto, UserDetailStatus } from "@models/user.model";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { RouteTrackingService } from "../../services/route-tracking/route-tracking.service";
import { trimFormValues } from "../../shared/utils/trim-form-values.util";
import { TrimOnBlurDirective } from "../../shared/directives/trim-on-blur.directive";
import { FirstErrorPipe } from "../../shared/pipes/first-error.pipe";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { MatCheckboxChange, MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialogRef } from "@angular/material/dialog";
import { Optional } from "@angular/core";
import { AuthService } from "../../services/auth/auth.service";
import { State, City } from "country-state-city";
import { Observable } from "rxjs/internal/Observable";
import { map, startWith } from "rxjs";
import { MatAutocompleteModule, MatAutocompleteTrigger } from "@angular/material/autocomplete";

interface State {
    id: number;
    value: string;
    viewValue: string;
}

enum AddressType {
    Home = "home",
    Work = "work",
    Other = "other",
}

interface AddressTypeOption {
    type: AddressType;
    label: string;
    icon: string;
}

@Component({
    selector: "app-user-details-form",
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        MatCheckboxModule,
        TrimOnBlurDirective,
        FirstErrorPipe,
        MatAutocompleteModule,
    ],
    templateUrl: "./user-details-form.component.html",
    styleUrl: "./user-details-form.component.scss",
})
export class UserDetailsFormComponent implements OnInit, OnDestroy {
    @Input() mode: "full" | "recipient-only" = "full";
    @Input() showActionButtons: boolean = true;
    @Input() hideAgeAndShowMobile: boolean = false;
    @Input() addressID!: number;
    @Input() fetchdetails: boolean = false;
    @Input() selectedAddressId?: number;
    @Input() noBgFormCard: boolean = false;
    autocompleteTrigger!: MatAutocompleteTrigger;

    @ViewChild("stateInput", { read: MatAutocompleteTrigger })
    stateTrigger!: MatAutocompleteTrigger;

    @ViewChild("recipientStateSelect", { read: MatAutocompleteTrigger })
    recipientStateTrigger!: MatAutocompleteTrigger;

    @ViewChild("cityInput", { read: MatAutocompleteTrigger })
    cityTrigger!: MatAutocompleteTrigger;

    @ViewChild("recipientCitySelect", { read: MatAutocompleteTrigger })
    recipientCityTrigger!: MatAutocompleteTrigger;

    public detailsForm!: FormGroup;
    public indianStates: State[] = [
        // States
        { id: 1, value: "Andhra Pradesh", viewValue: "Andhra Pradesh" },
        { id: 2, value: "Arunachal Pradesh", viewValue: "Arunachal Pradesh" },
        { id: 3, value: "Assam", viewValue: "Assam" },
        { id: 4, value: "Bihar", viewValue: "Bihar" },
        { id: 5, value: "Chhattisgarh", viewValue: "Chhattisgarh" },
        { id: 6, value: "Goa", viewValue: "Goa" },
        { id: 7, value: "Gujarat", viewValue: "Gujarat" },
        { id: 8, value: "Haryana", viewValue: "Haryana" },
        { id: 9, value: "Himachal Pradesh", viewValue: "Himachal Pradesh" },
        { id: 10, value: "Jharkhand", viewValue: "Jharkhand" },
        { id: 11, value: "Karnataka", viewValue: "Karnataka" },
        { id: 12, value: "Kerala", viewValue: "Kerala" },
        { id: 13, value: "Madhya Pradesh", viewValue: "Madhya Pradesh" },
        { id: 14, value: "Maharashtra", viewValue: "Maharashtra" },
        { id: 15, value: "Manipur", viewValue: "Manipur" },
        { id: 16, value: "Meghalaya", viewValue: "Meghalaya" },
        { id: 17, value: "Mizoram", viewValue: "Mizoram" },
        { id: 18, value: "Nagaland", viewValue: "Nagaland" },
        { id: 19, value: "Odisha", viewValue: "Odisha" },
        { id: 20, value: "Punjab", viewValue: "Punjab" },
        { id: 21, value: "Rajasthan", viewValue: "Rajasthan" },
        { id: 22, value: "Sikkim", viewValue: "Sikkim" },
        { id: 23, value: "Tamil Nadu", viewValue: "Tamil Nadu" },
        { id: 24, value: "Telangana", viewValue: "Telangana" },
        { id: 25, value: "Tripura", viewValue: "Tripura" },
        { id: 26, value: "Uttar Pradesh", viewValue: "Uttar Pradesh" },
        { id: 27, value: "Uttarakhand", viewValue: "Uttarakhand" },
        { id: 28, value: "West Bengal", viewValue: "West Bengal" },

        // Union Territories
        {
            id: 29,
            value: "Andaman and Nicobar Islands",
            viewValue: "Andaman and Nicobar Islands",
        },
        { id: 30, value: "Chandigarh", viewValue: "Chandigarh" },
        {
            id: 31,
            value: "Dadra and Nagar Haveli and Daman and Diu",
            viewValue: "Dadra and Nagar Haveli and Daman and Diu",
        },
        { id: 32, value: "Delhi", viewValue: "Delhi" },
        { id: 33, value: "Jammu and Kashmir", viewValue: "Jammu and Kashmir" },
        { id: 34, value: "Ladakh", viewValue: "Ladakh" },
        { id: 35, value: "Lakshadweep", viewValue: "Lakshadweep" },
        { id: 36, value: "Puducherry", viewValue: "Puducherry" },
    ];

    public AddressType = AddressType;
    public showDefaultAddress: boolean = false;
    public isSetAsDefault: boolean = false;

    public addressTypeOptions: AddressTypeOption[] = [
        {
            type: AddressType.Home,
            label: "Home",
            icon: "home",
        },
        {
            type: AddressType.Work,
            label: "Work",
            icon: "briefcase",
        },
        {
            type: AddressType.Other,
            label: "Other",
            icon: "location",
        },
    ];

    private recipientValidators = {
        name: [trimmedRequired(), nameValidator()],
        mobileNumber: [trimmedRequired(), indianPhoneNumber()],
        houseNumber: [trimmedRequired()],
        pincode: [trimmedRequired(), indianPincode()],
        address: [trimmedRequired()],
        city: [trimmedRequired()],
        state: [trimmedRequired()],
        addressType: [trimmedRequired()],
    };

    cities: any[] = [];

    states = State.getStatesOfCountry("IN");

    filteredStates!: Observable<any[]>;

    filteredCities!: Observable<any[]>;

    constructor(
        private userService: UserService,
        private snackBar: MatSnackBar,
        private router: Router,
        private routerService: RouteTrackingService,
        private fb: NonNullableFormBuilder,
        private auth: AuthService,
        @Optional() private dialogRef: MatDialogRef<UserDetailsFormComponent>,
    ) {}

    ngOnInit(): void {
        if (this.auth.isUserLoggedIn) {
            this.userService.isUserDetailsExist().subscribe((response) => {
                this.showDefaultAddress = response;
            });
        } else {
            this.showDefaultAddress = false;
        }

        this.buildForm();

        this.setupStateCityDependency();

        this.filteredStates = this.detailsForm.get("state")!.valueChanges.pipe(
            startWith(""),
            map((value) => this.filterStates(value || "")),
        );

        this.filteredCities = this.detailsForm.get("city")!.valueChanges.pipe(
            startWith(""),
            map((value) => this.filterCities(value || "")),
        );

        if (this.mode === "recipient-only") {
            this.clearParentValidators();
            if (this.addressID) {
                this.loadUserDetails();
            } else {
                this.toggleRecipientValidators(true);
            }
        }

        this.detailsForm
            .get("useDifferentRecipientInfo")!
            .valueChanges.subscribe((enabled) => this.toggleRecipientValidators(enabled));
    }

    private filterStates(value: string) {
        const filterValue = value.toLowerCase();

        return this.states.filter((state) => state.name.toLowerCase().includes(filterValue));
    }

    private filterCities(value: string) {
        const filterValue = value.toLowerCase();

        return this.cities.filter((city) => city.name.toLowerCase().includes(filterValue));
    }

    public onStateSelected(stateName: string) {
        const state = this.states.find((s) => s.name === stateName);

        const cityControl = this.detailsForm.get("city");

        if (state) {
            this.cities = City.getCitiesOfState("IN", state.isoCode);

            cityControl?.reset();

            if (this.cities.length > 0) {
                cityControl?.enable();
            } else {
                cityControl?.disable();
            }
        } else {
            this.cities = [];
            cityControl?.reset();
            cityControl?.disable();
        }
    }

    private buildForm(): void {
        this.detailsForm = this.fb.group({
            name: ["", [trimmedRequired(), nameValidator()]],
            email: ["", [trimmedRequired(), Validators.email]],
            age: [{ value: "", disabled: this.hideAgeAndShowMobile }, [trimmedRequired(), digitsOnly()]],
            userMobileNumber: [{ value: "", disabled: !this.hideAgeAndShowMobile }, Validators.required],
            houseNumber: ["", [trimmedRequired()]],
            pincode: ["", [trimmedRequired(), indianPincode()]],
            address: ["", [trimmedRequired()]],
            city: [{ value: "", disabled: true }, [trimmedRequired()]],
            state: ["", [trimmedRequired()]],
            addressType: [AddressType.Home, [trimmedRequired()]],
            isDefault: [true],

            useDifferentRecipientInfo: [false],

            recipient: this.fb.group({
                name: [""],
                mobileNumber: [""],
                houseNumber: [""],
                pincode: [""],
                address: [""],
                city: [{ value: "", disabled: true }, [trimmedRequired()]],
                state: [""],
                addressType: [AddressType.Home],
                isDefault: [false],
            }),
        });
    }

    private setupStateCityDependency(): void {
        const stateControl = this.detailsForm.get("state")!;
        const cityControl = this.detailsForm.get("city")!;

        stateControl.valueChanges.subscribe((state: string) => {
            // Clear previous city whenever state changes
            cityControl.reset("", { emitEvent: false });

            if (state?.trim()) {
                cityControl.enable({ emitEvent: false });
            } else {
                cityControl.disable({ emitEvent: false });
            }
        });

        const recipientGroup = this.detailsForm.get("recipient") as FormGroup;

        const recipientState = recipientGroup.get("state")!;

        const recipientCity = recipientGroup.get("city")!;

        // Make sure recipient city is disabled initially
        recipientCity.disable({ emitEvent: false });

        recipientState.valueChanges.subscribe((state: string) => {
            // Clear previous city whenever recipient state changes
            recipientCity.reset("", { emitEvent: false });

            if (state?.trim()) {
                recipientCity.enable({ emitEvent: false });
            } else {
                recipientCity.disable({ emitEvent: false });
            }
        });
    }

    private clearParentValidators(): void {
        const parentFields = [
            "name",
            "email",
            "age",
            "houseNumber",
            "pincode",
            "address",
            "city",
            "state",
            "addressType",
        ];

        parentFields.forEach((field) => {
            const control = this.detailsForm.get(field);
            control?.clearValidators();
            control?.updateValueAndValidity({ emitEvent: false });
        });
    }

    private toggleRecipientValidators(enabled: boolean): void {
        const group = this.detailsForm.get("recipient") as FormGroup;

        Object.entries(group.controls).forEach(([key, control]) => {
            enabled
                ? control.setValidators(this.recipientValidators[key as keyof typeof this.recipientValidators] ?? [])
                : control.clearValidators();

            control.updateValueAndValidity({
                emitEvent: false,
            });
        });

        if (!enabled) {
            group.reset(
                {
                    name: "",
                    mobileNumber: "",
                    houseNumber: "",
                    pincode: "",
                    address: "",
                    city: "",
                    state: "",
                    addressType: AddressType.Home,
                    isDefault: false,
                },
                { emitEvent: false },
            );

            // Recipient city must remain disabled
            group.get("city")?.disable({
                emitEvent: false,
            });
        }
    }

    public allowOnlyNumbers(event: KeyboardEvent): void {
        const allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Tab", "Delete"];

        if (allowedKeys.includes(event.key)) {
            return;
        }

        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
        }
    }

    public openStateSelect(isRecipient: boolean = false): void {
        if (isRecipient) {
            this.recipientStateTrigger?.openPanel();
        } else {
            this.stateTrigger?.openPanel();
        }
    }

    public openCitySelect(isRecipient: boolean = false): void {
        if (isRecipient) {
            this.recipientCityTrigger?.openPanel();
        } else {
            this.cityTrigger?.openPanel();
        }
    }

    trackByStateId(index: number, item: any) {
        return item.value;
    }

    private openSnackBar(message: string, action: string) {
        this.snackBar.open(message, action, {
            duration: SnackBarConfig.Duration.MEDIUM,
        });
    }
    public onSkip(): void {
        if (this.dialogRef) {
            this.dialogRef?.close(false);
        } else {
            this.navigateBack();
        }
    }

    public navigateBack(): void {
        const navigatePreviousUrl = this.routerService.getPreviousUrl(); // To navigate the previous route
        this.router.navigate([navigatePreviousUrl]);
    }

    public onSubmit(): void {
        if (this.detailsForm.invalid) {
            this.detailsForm.markAllAsTouched();
            return;
        }

        const value = trimFormValues(this.detailsForm.value);

        if (this.mode === "recipient-only") {
            const payload = this.createRecipientPayload(value);
            this.handleAddress(payload);
        } else {
            const payload = this.createUserPayload(value);
            this.handleUser(payload);
        }
    }

    private createRecipientPayload(value: any): BaseAddress {
        return {
            id: this.addressID,
            name: value.recipient.name,
            mobileNumber: value.recipient.mobileNumber,
            houseNumber: value.recipient.houseNumber,
            pincode: Number(value.recipient.pincode),
            address: value.recipient.address,
            city: value.recipient.city,
            state: value.recipient.state,
            addressType: value.recipient.addressType,
        };
    }

    private createUserPayload(value: any): UserDetailsDto {
        return {
            userDetails: {
                name: value.name,
                email: value.email,
                age: Number(value.age),
                userMobileNumber: value.userMobileNumber,
            },
            addressDetails: {
                houseNumber: value.houseNumber,
                pincode: Number(value.pincode),
                address: value.address,
                city: value.city,
                state: value.state,
                addressType: value.addressType,
                isDefault: value.isDefault,
            },
            recipient: value.useDifferentRecipientInfo ? { ...value.recipient } : null,
        };
    }

    private handleAddress(payload: BaseAddress) {
        if (this.addressID) {
            this.userService.updateAddress(payload).subscribe({
                next: () => {
                    this.dialogRef?.close(true);
                },
            });
        } else {
            this.userService.addAddress(payload).subscribe({
                next: () => {
                    this.openSnackBar(UserDetailStatus.Add, SnackBarConfig.Actions.CLOSE);
                    this.dialogRef?.close(true);
                },
            });
        }
    }

    private handleUser(payload: UserDetailsDto) {
        this.userService.addUserDetails(payload).subscribe(() => {
            this.openSnackBar(UserDetailStatus.Add, SnackBarConfig.Actions.CLOSE);
            this.navigateBack();
        });
    }

    private loadUserDetails(): void {
        this.userService.getAddress(this.addressID).subscribe((res) => {
            this.detailsForm.get("recipient")?.patchValue({
                name: res.name,
                mobileNumber: res.mobileNumber,
                houseNumber: res.houseNumber,
                pincode: res.pincode,
                address: res.address,
                city: res.city,
                state: res.state,
                addressType: res.addressType,
                isDefault: this.showDefaultAddress ? (this.isSetAsDefault ? true : res?.isDefault) : true,
            });

            this.onStateSelected(res.state);
        });
    }

    public getFormValue() {
        return this.detailsForm.value;
    }

    public isValid() {
        return this.detailsForm.valid;
    }

    public toggleDefault(event: MatCheckboxChange): void {
        this.isSetAsDefault = event.checked;
    }

    ngOnDestroy(): void {}
}
